import { gql } from '@apollo/client';
import { Typography, Button, CircularProgress, Paper, Link } from '@mui/material';
import { FC, useEffect } from 'react';
import styled from 'styled-components';

import { WizardStepProps } from 'components/wizard';
import { useConnectAccountContext } from 'context/connectAccountContext';
import { PublicPartnerType, useRecognizePublicPartnerMutation } from 'graphql/types';
import { OAuthErrorInfo } from './OAuthErrorInfo';
import { createRedirectUri } from 'helpers/oauth';
import { useNavigate } from 'react-router-dom';

export type AccountRecognitionStepProps = WizardStepProps;

export const AccountRecognitionStep: FC<AccountRecognitionStepProps> = ({ nextStep, setValue }) => {
  const navigate = useNavigate();
  const { accountType, oauth, marketplace, isAuthenticating } = useConnectAccountContext();
  const [recognizeAccount, { data, loading, error }] = useRecognizePublicPartnerMutation();

  useEffect(() => {
    switch (oauth?.data.accountType) {
      case PublicPartnerType.Advertising:
        recognizeAccount({
          variables: {
            code: oauth?.data.code,
            redirectUri: createRedirectUri(),
            adsProfileId: 'TODO', // Replace with actual profile ID
            marketplace: oauth?.data.marketplace,
            accountType,
          },
        });
        break;

      case PublicPartnerType.Sellercentral:
      case PublicPartnerType.Vendorcentral:
        recognizeAccount({
          variables: {
            code: oauth?.data.code,
            redirectUri: createRedirectUri(),
            marketplace: oauth?.data.marketplace,
            accountType,
            sellingPartnerId: oauth?.data.sellingPartnerId,
          },
        });
        break;
    }
  }, [accountType, marketplace, oauth, recognizeAccount]);

  const renderAccountTypeString = () => {
    switch (accountType) {
      case PublicPartnerType.Sellercentral:
        return 'Seller Central';
      case PublicPartnerType.Vendorcentral:
        return 'Vendor Central';
      case PublicPartnerType.Advertising:
        return 'Advertising';
      default:
        return 'Unknown';
    }
  };

  const renderAccountDetails = () => {
    if (!data) return null;
    return (
      <>
        <Typography variant="h6" gutterBottom>
          Account Details
        </Typography>
        {data.recognizePublicPartner.accountName && (
          <DetailRow>
            <Typography variant="subtitle2">Account Name:</Typography>
            <Typography>{data.recognizePublicPartner.accountName}</Typography>
          </DetailRow>
        )}
        {data.recognizePublicPartner.accountId && (
          <DetailRow>
            <Typography variant="subtitle2">Account ID:</Typography>
            <Typography>{data.recognizePublicPartner.accountId}</Typography>
          </DetailRow>
        )}
        {data.recognizePublicPartner.entityId && (
          <DetailRow>
            <Typography variant="subtitle2">Entity ID:</Typography>
            <Typography>{data.recognizePublicPartner.entityId}</Typography>
          </DetailRow>
        )}
        <DetailRow>
          <Typography variant="subtitle2">Account Type:</Typography>
          <Typography>{renderAccountTypeString()}</Typography>
        </DetailRow>
        {data.recognizePublicPartner.marketPlaces && data.recognizePublicPartner.marketPlaces.length > 0 && (
          <DetailRow>
            <Typography variant="subtitle1">Marketplaces</Typography>
            <Typography>{data.recognizePublicPartner.marketPlaces.join(', ')}</Typography>
          </DetailRow>
        )}
        {data.recognizePublicPartner.sellerItems && data.recognizePublicPartner.sellerItems.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Sample Items:
            </Typography>
            <div style={{ marginLeft: 8 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 400 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>ASIN</th>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>SKU</th>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>Brand</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recognizePublicPartner.sellerItems.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>
                        {item.asinUri ? (
                          <Link href={item.asinUri} target="_blank">
                            {item.asin}
                          </Link>
                        ) : (
                          item.asin
                        )}
                      </td>
                      <td style={{ padding: '6px 12px' }}>{item.sku || <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={{ padding: '6px 12px' }}>{item.name || <span style={{ color: '#aaa' }}>—</span>}</td>
                      <td style={{ padding: '6px 12px' }}>{item.brand || <span style={{ color: '#aaa' }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {data.recognizePublicPartner.vendorOrders && data.recognizePublicPartner.vendorOrders.length > 0 && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Sample Items:
            </Typography>
            <div style={{ overflowX: 'auto', marginLeft: 8 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 400 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>ASIN</th>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                      Order Number
                    </th>
                    <th style={{ padding: '6px 12px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                      Order Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.recognizePublicPartner.vendorOrders.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>
                        {item.asinUri ? (
                          <Link href={item.asinUri} target="_blank">
                            {item.asin}
                          </Link>
                        ) : (
                          item.asin
                        )}
                      </td>
                      <td style={{ padding: '6px 12px' }}>
                        {item.orderNumber || <span style={{ color: '#aaa' }}>—</span>}
                      </td>
                      <td style={{ padding: '6px 12px' }}>
                        {item.orderDate || <span style={{ color: '#aaa' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {data.recognizePublicPartner.campaigns && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Campaigns:
            </Typography>
            <div style={{ marginLeft: 16 }}>
              <Typography variant="subtitle2">Sponsored Products (SP):</Typography>
              {Array.isArray(data.recognizePublicPartner.campaigns.spCampaigns) &&
              data.recognizePublicPartner.campaigns.spCampaigns.length > 0 ? (
                <ul style={{ marginTop: 0 }}>
                  {data.recognizePublicPartner.campaigns.spCampaigns.map((c, i) => (
                    <li key={i}>
                      <Typography variant="body2">{c}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No Sponsored Products campaigns found.
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Sponsored Brands (SB):
              </Typography>
              {Array.isArray(data.recognizePublicPartner.campaigns.sbCampaigns) &&
              data.recognizePublicPartner.campaigns.sbCampaigns.length > 0 ? (
                <ul style={{ marginTop: 0 }}>
                  {data.recognizePublicPartner.campaigns.sbCampaigns.map((c, i) => (
                    <li key={i}>
                      <Typography variant="body2">{c}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No Sponsored Brands campaigns found.
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Sponsored Display (SD):
              </Typography>
              {Array.isArray(data.recognizePublicPartner.campaigns.sdCampaigns) &&
              data.recognizePublicPartner.campaigns.sdCampaigns.length > 0 ? (
                <ul style={{ marginTop: 0 }}>
                  {data.recognizePublicPartner.campaigns.sdCampaigns.map((c, i) => (
                    <li key={i}>
                      <Typography variant="body2">{c}</Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No Sponsored Display campaigns found.
                </Typography>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  if (!isAuthenticating || !oauth?.success) {
    return <Typography color="error">Authentication is required before recognizing account information</Typography>;
  }

  return (
    <StyledContainer>
      <Typography variant="subtitle1" gutterBottom>
        Please confirm this is the correct Amazon account you want to connect
      </Typography>
      {loading ? (
        <LoadingContainer>
          <CircularProgress size={40} />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Fetching account information...
          </Typography>
        </LoadingContainer>
      ) : error ? (
        <ErrorContainer>
          <OAuthErrorInfo errorMessage={error.message} success={false} connect={async () => {}} />
        </ErrorContainer>
      ) : (
        <AccountInfoContainer>{renderAccountDetails()}</AccountInfoContainer>
      )}
      <ButtonContainer>
        <Button onClick={() => navigate('/connect')} variant="contained" color="error" style={{ marginRight: 8 }}>
          Start Over
        </Button>
        {data?.recognizePublicPartner.token && (
          <Button
            onClick={() => {
              setValue('refreshToken', data.recognizePublicPartner.token);
              nextStep();
            }}
            variant="contained"
            color="primary"
            disabled={loading || !!error}
          >
            Confirm and Continue
          </Button>
        )}
      </ButtonContainer>
    </StyledContainer>
  );
};

export const RecognizePublicPartnerMutation = gql`
  mutation RecognizePublicPartner(
    $code: String!
    $redirectUri: String!
    $marketplace: String!
    $accountType: PublicPartnerType!
    $sellingPartnerId: String
    $adsProfileId: String
  ) {
    recognizePublicPartner(
      code: $code
      redirectUri: $redirectUri
      marketPlace: $marketplace
      accountType: $accountType
      sellingPartnerId: $sellingPartnerId
      adsProfileId: $adsProfileId
    ) {
      accountId
      entityId
      accountName
      marketPlaces
      sellerItems {
        asin
        asinUri
        sku
        name
        brand
      }
      vendorOrders {
        asin
        asinUri
        orderDate
        orderNumber
      }
      campaigns {
        spCampaigns
        sbCampaigns
        sdCampaigns
      }
      token
    }
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 1 0%;
  min-height: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px 0;
`;

const AccountInfoContainer = styled(Paper)`
  flex: 1 1 0%;
  min-height: 0;
  padding: 16px;
  margin: 16px 0;
  overflow-y: auto;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 8px;

  & > *:first-child {
    min-width: 150px;
    font-weight: 500;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;
